import React, { MouseEventHandler, useCallback, useMemo, useState } from "react";

import { es, enUS } from "date-fns/locale";

import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import "./datePicker.css";

import { useLocaleProvider } from "../../../components/locale-provider";
import { IntlShape, useIntl } from "react-intl";
import dayjs from "dayjs";
import { LOCAL_STORAGE_KEYS } from "../../witadmin/Common/constants";
import {
  CalendarBox,
  InputContainer,
  StyledDatePicker,
} from "../../ariManagement/updateForm/dateRange/styles";
import { CalendarIcon } from "../../ariManagement/shared/assets/CalendarIcon";
import { RankingProp } from "../types/types";
import { modalMessages } from "../messages";
import CloseSvg from "../assets/close";
import {useTheme} from "styled-components";

interface ModalDetailMyMotorProps {
  title: string;
  description: string;
  ranking: RankingProp[];
  handleClose: MouseEventHandler<HTMLButtonElement> | undefined;
  value?: string | number | boolean;
  intl: IntlShape;
}

interface ModalTailwindCustomDateProps {
  handleClose: () => void;
  onHandleSearch: ({
    e,
    startDate,
    endDate,
  }: {
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>;
    startDate: string;
    endDate: string;
  }) => void;
  currentStartDate: string;
  currentEndDate: string;
}

function getCloseIcon() {
  return (
    <CloseSvg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    />
  );
}

export const ModalDetailMyMotor = ({
  title,
  description,
  ranking,
  handleClose,
  value,
  intl,
}: ModalDetailMyMotorProps) => {
  const theme = useTheme();

  return (
    <div className="relative z-10" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="fixed inset-0 bg-gray-500 bg-opacity-40 transition-opacity"></div>
      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl ">
            <header className={`w-full h-16 bg-[${theme.buttonColor}] text-white flex items-center p-4 justify-between`}>
              <span className="font-semibold text-xl">{title}</span>
              <button onClick={handleClose}>{getCloseIcon()}</button>
            </header>
            <div className="p-4">
              <p className="mb-[35px]">
                {description} <span className="font-semibold">{value}</span>
              </p>
              {ranking.length > 0 && (
                <div>
                  <p className="font-semibold border-b border-[#B3B3B3] mb-[20px] pb-[10px]">
                    {intl.formatMessage(modalMessages.ranking)}
                  </p>
                  <div className="flex flex-col">
                    {ranking.map(({ name }, index: number) => (
                      <span className={`${index === 0 && "font-semibold"}`} key={`${name}`}>{`#${
                        index + 1
                      } ${name}`}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-center pt-[40px]">
                <button
                  onClick={handleClose}
                  className={`bg-[${theme.buttonColor}] text-white px-2.5 py-1.5 rounded`}>
                  {intl.formatMessage(modalMessages.back)}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ModalTailwindCustomDate = ({
  handleClose,
  onHandleSearch,
  currentStartDate,
  currentEndDate,
}: ModalTailwindCustomDateProps) => {
  const ITERATION_MONTHS = 24;
  const intl = useIntl();
  const [startDate, setStartDate] = useState(dayjs(currentStartDate, "YYYY-MM-DD").toDate());
  const [endDate, setEndDate] = useState<Date | null>(dayjs(currentEndDate, "YYYY-MM-DD").toDate());
  const localeProvider = useLocaleProvider();
const theme = useTheme();

  const lastMonts = useMemo(() => {
    return Array.from({ length: ITERATION_MONTHS }, (_, i) => i + 1);
  }, []);

  const saveItemInLocalStorage = useCallback((startDate, endDate) => {
    const currentCustomSearch =
      window.sessionStorage.getItem(LOCAL_STORAGE_KEYS.DASHBOARD_CUSTOM_SEARCH) || "";
    const dates = `${dayjs(startDate).format("YYYY/MM/DD")} - ${dayjs(endDate).format(
      "YYYY/MM/DD"
    )}`;
    window.sessionStorage.setItem(
      LOCAL_STORAGE_KEYS.DASHBOARD_CUSTOM_SEARCH,
      `${currentCustomSearch}${
        currentCustomSearch && currentCustomSearch?.length > 0 ? "," : ""
      } ${dates}`
    );
  }, []);

  const handleSearch = useCallback(
    e => {
      if (startDate && endDate) {
        saveItemInLocalStorage(startDate, endDate);
        onHandleSearch({
          e,
          startDate: dayjs(startDate).format("YYYY-MM-DD"),
          endDate: dayjs(endDate).format("YYYY-MM-DD"),
        });
        handleClose();
      }
    },
    [endDate, handleClose, startDate, onHandleSearch, saveItemInLocalStorage]
  );

  const onHandleClickLastDate = useCallback(
    ({ e, item }: { e: React.MouseEvent<HTMLSpanElement, MouseEvent>; item: number }) => {
      const currentDay = dayjs()
        .add(item * -1, "M")
        .format("YYYY-MM-DD");
      const startDate = dayjs(currentDay).startOf("month").format("YYYY-MM-DD");
      const endDate = dayjs(currentDay).endOf("month").format("YYYY-MM-DD");
      saveItemInLocalStorage(startDate, endDate);
      onHandleSearch({ e, startDate, endDate });
      handleClose();
    },
    [handleClose, onHandleSearch, saveItemInLocalStorage]
  );

  const onHandleClickRecentsDate = useCallback(
    ({ e, date }: { e: React.MouseEvent<HTMLSpanElement, MouseEvent>; date: string }) => {
      e.preventDefault();
      const splitDate = date.split(" - ");
      const startDate = dayjs(splitDate[0]).format("YYYY-MM-DD");
      const endDate = dayjs(splitDate[1]).format("YYYY-MM-DD");
      onHandleSearch({ e, startDate, endDate });
      handleClose();
    },
    [handleClose, onHandleSearch]
  );

  const changeStartDate = useCallback((newDate: Date) => {
    setStartDate(newDate);
    setEndDate(null);
  }, []);

  const changeEndDate = useCallback((newDate: Date) => {
    setEndDate(newDate);
  }, []);

  return (
    <div className="relative z-10" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="fixed inset-0 bg-gray-500 bg-opacity-40 transition-opacity"></div>
      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:max-h-[500px] sm:h-[500px]">
            <header className={`bg-[${theme.buttonColor}] w-full h-16 text-white flex items-center p-4 justify-between`}>
              <span className="font-semibold text-xl">{intl.formatMessage(modalMessages.title)}</span>
              <button onClick={handleClose}>{getCloseIcon()}</button>
            </header>
            <div className="p-4 flex">
              <article className="w-[70%]">
                <div className="flex items-center justify-between pr-2">
                  <InputContainer>
                    <CalendarBox>
                      <CalendarIcon />
                    </CalendarBox>
                    <StyledDatePicker
                      selected={startDate}
                      placeholderText={intl.formatMessage(modalMessages.fromCalendar)}
                      dateFormat={"dd-MM-yyyy"}
                      onChange={changeStartDate}
                      locale={localeProvider.currentLocale === "en-US" ? enUS : es}
                    />
                  </InputContainer>
                  <InputContainer>
                    <CalendarBox>
                      <CalendarIcon />
                    </CalendarBox>
                    <StyledDatePicker
                      selected={endDate}
                      placeholderText={intl.formatMessage(modalMessages.toCalendar)}
                      dateFormat={"dd-MM-yyyy"}
                      onChange={changeEndDate}
                      minDate={startDate}
                      maxDate={dayjs(startDate).add(31, "d").toDate()}
                      locale={localeProvider.currentLocale === "en-US" ? enUS : es}
                    />
                  </InputContainer>
                  <button
                    className={`bg-[${theme.buttonColor}] text-xs rounded py-1.5 px-2.5 text-white h-[30px]`}
                    onClick={handleSearch}
                    disabled={!(startDate && endDate)}>
                    {intl.formatMessage(modalMessages.search)}
                  </button>
                </div>
                <div className={`pt-3 pb-4 text-[${theme.buttonColor}]`}>
                  <span className="text-sm">{intl.formatMessage(modalMessages.allowedDays)}</span>
                </div>

                <h4 className="text-lg font-semibold">
                  {intl.formatMessage(modalMessages.recentSearches)}
                </h4>
                <div className="max-h-[264px] flex flex-col overflow-x-auto">
                  {window.sessionStorage.getItem(LOCAL_STORAGE_KEYS.DASHBOARD_CUSTOM_SEARCH) ? (
                    window.sessionStorage
                      .getItem(LOCAL_STORAGE_KEYS.DASHBOARD_CUSTOM_SEARCH)
                      ?.split(",")
                      .map((date: string) => (
                        <span
                          onClick={e => onHandleClickRecentsDate({ e, date })}
                          key={date}
                          className="text-base pb-2 cursor-pointer">
                          {date}
                        </span>
                      ))
                  ) : (
                    <span className="text-base pb-2">
                      {" "}
                      {intl.formatMessage(modalMessages.noRecords)}
                    </span>
                  )}
                </div>
              </article>
              <article className="w-[30%] border-l border-[#AFAFAF] pl-[20px] flex flex-col max-h-[400px] overflow-x-auto">
                <h4 className={`text-[${theme.buttonColor}] text-lg font-semibold`}>
                  {intl.formatMessage(modalMessages.lastMonth)}
                </h4>
                {lastMonts.map(item => (
                  <span
                    className="text-base cursor-pointer text-capitalize"
                    onClick={e => onHandleClickLastDate({ e, item })}>
                    {dayjs()
                      .add(item * -1, "M")
                      .format("MMMM YYYY")}
                  </span>
                ))}
              </article>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ModalTailwindCalendar = ({
  title,
  handleClose,
  selectedDays,
}: {
  title: string;
  handleClose: MouseEventHandler<HTMLButtonElement>;
  selectedDays: Date[];
}) => {
  const intl = useIntl();
  const localeProvider = useLocaleProvider();
  const theme = useTheme();

  return (
    <div className="relative z-10 w-full">
      <div className="fixed inset-0 bg-gray-500 bg-opacity-40 transition-opacity"></div>
      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 ">
            <header className={`bg-[${theme.buttonColor}] w-full h-16 text-white flex items-center p-4 justify-between`}>
              <span className="font-semibold text-xl">{title}</span>
              <button onClick={handleClose}>{getCloseIcon()}</button>
            </header>
            <article className="p-4">
              <DayPicker
                selected={selectedDays}
                defaultMonth={selectedDays[0]}
                numberOfMonths={2}
                pagedNavigation
                showOutsideDays
                fixedWeeks
                locale={localeProvider.currentLocale === "en-US" ? enUS : es}
              />
            </article>
            <footer className="px-4 pb-8">
              <div className="w-full flex flex-row justify-center gap-8">
                <div className="flex flex-row items-center">
                  <div className={`bg-[${theme.buttonColor}] h-4 w-4 mr-4`} />
                  <span className="font-semibold text-sm text-[#2E2F36]">
                    {intl.formatMessage(modalMessages.notAvailable)}
                  </span>
                </div>
                <div className="flex flex-row items-center">
                  <div className=" h-4 w-4 bg-[#5C5C5C] mr-4" />
                  <span className="font-semibold text-sm text-[#2E2F36]">
                    {intl.formatMessage(modalMessages.available)}
                  </span>
                </div>
              </div>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
};
